import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { BrandLogo } from "@/components/BrandLogo";
import { consumeNextPath } from "@/lib/nextPath";
import { useInitialFocus } from "@/hooks/useInitialFocus";

export function TwoFactorPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const codeRef = useInitialFocus<HTMLInputElement>();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error: verifyError } = useBackupCode
      ? await authClient.twoFactor.verifyBackupCode({ code })
      : await authClient.twoFactor.verifyTotp({ code });
    setLoading(false);
    if (verifyError) {
      setError(authErrorMessage(verifyError, "Invalid code."));
      return;
    }
    navigate({ to: consumeNextPath() ?? "/" });
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <BrandLogo className="auth-logo" size={22} />
        <h1 className="auth-title">Two-factor verification</h1>
        <p className="auth-subtitle">
          {useBackupCode
            ? "Enter one of your backup codes."
            : "Enter the 6-digit code from your authenticator app."}
        </p>

        <form className="panel" onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="code">
              {useBackupCode ? "Backup code" : "Authenticator code"}
            </label>
            <input
              id="code"
              ref={codeRef}
              className={`input mono ${useBackupCode ? "backup-code-input" : "otp-input"}`}
              required
              inputMode={useBackupCode ? "text" : "numeric"}
              maxLength={useBackupCode ? 20 : 6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={useBackupCode ? "xxxxx-xxxxx" : "123456"}
            />
          </div>
          <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
            {loading ? "Verifying…" : "Verify"}
          </button>
          {error && <div className="error-text" role="alert">{error}</div>}
        </form>

        <div className="auth-text-action">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setUseBackupCode((v) => !v);
              setCode("");
              setError(null);
            }}
          >
            {useBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
          </button>
        </div>
      </div>
    </div>
  );
}
